// =========================================================================
// REFERENCE DATA LIBRARY — Phase R2b
//
// As of R2b, all picker categories source from the merged seed dataset
// (`merged_library_v1.json`) via the R2a typed selectors. The pilot JSON
// files (`tubulars.json`, `pumps.json`) remain on disk for rollback safety
// but are no longer imported.
// =========================================================================
import { loadMergedLibrary as _loadMergedLib } from "./normalize.js";

export const REFERENCE_LIBRARY_VERSION = "v1.0";
export const REFERENCE_LAST_UPDATED_ISO = "2026-02-15";

// Five allowed source labels — UI badges & test sweep must match these.
export const REFERENCE_SOURCE_ENUM = [
  "user-entered",
  "rig-custom",
  "formula-derived",
  "manufacturer-chart",
  "standard-derived",
];

// Canonical persistent disclaimer banner, kept here so the page and any
// later headless export use the same wording.
export const REFERENCE_DISCLAIMER =
  "Reference Data Library — support data only. Reference items are provided as a starting point for calculations. Final values are the user's responsibility and must be verified against rig-specific tally books, manufacturer specifications, and company procedures. Do not use this library as the sole source of truth for any operational decision.";

// --- Capacity helpers (deterministic; same constants used by balancedplug.js) -
const ID2_DIVISOR = 1029.4;
export const FT3_PER_BBL = 5.6146;

export function insideCapacityBblPerFt(id_in) {
  if (!Number.isFinite(id_in) || id_in <= 0) return null;
  return (id_in * id_in) / ID2_DIVISOR;
}

export function annularCapacityBblPerFt(hole_or_casing_id_in, pipe_od_in) {
  if (!Number.isFinite(hole_or_casing_id_in) || hole_or_casing_id_in <= 0) return null;
  if (!Number.isFinite(pipe_od_in) || pipe_od_in <= 0) return null;
  if (hole_or_casing_id_in <= pipe_od_in) return null;
  return (hole_or_casing_id_in * hole_or_casing_id_in - pipe_od_in * pipe_od_in) / ID2_DIVISOR;
}

export function displacementBblPerFt(od_in, id_in) {
  if (!Number.isFinite(od_in) || od_in <= 0) return null;
  if (!Number.isFinite(id_in) || id_in <= 0) return null;
  if (od_in <= id_in) return null;
  return (od_in * od_in - id_in * id_in) / ID2_DIVISOR;
}

// --- Pump-output helper (Triplex single-acting, formula-derived only) --------
// Theoretical: Q_per_stk_gal = 3 × (π/4) × D_liner² × S × η / 231
// Convert gal → bbl by dividing by 42:
// Q_bbl_per_stk = 3 × π × D_liner² × S × η / (4 × 231 × 42)
export function triplexSingleActingBblPerStk({ liner_diameter_in, stroke_length_in, volumetric_efficiency }) {
  if (!Number.isFinite(liner_diameter_in) || liner_diameter_in <= 0) return null;
  if (!Number.isFinite(stroke_length_in) || stroke_length_in <= 0) return null;
  if (!Number.isFinite(volumetric_efficiency) || volumetric_efficiency <= 0 || volumetric_efficiency > 1) return null;
  const gallons = 3 * (Math.PI / 4) * liner_diameter_in * liner_diameter_in * stroke_length_in * volumetric_efficiency / 231;
  return gallons / 42;
}

// --- Library access (R2b: sourced from merged_library_v1.json via R2a loader)
const _DATASET = "rsc_seed_v1";
const _DATASET_VERSION = "1.0.0";

function _decorateBase(item, kind) {
  return {
    ...item,
    kind,
    source: _DATASET,
    version: _DATASET_VERSION,
    lastUpdatedISO: REFERENCE_LAST_UPDATED_ISO,
  };
}

function _adaptDrillPipe(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.nominal_size || ""} · ${it.weight_lbft} ppf · ${it.grade}${it.connection_type ? ` · ${it.connection_type}` : ""}`,
      od_in: it.od_in,
      id_in: it.id_in,
      weight_lbft: it.weight_lbft,
      grade: it.grade,
      connection_type: it.connection_type,
      notes: `Body OD ${it.od_in} in · ID ${it.id_in} in · ${it.grade} grade. Verify against rig tally.`,
    },
    "drill_pipe"
  );
}
function _adaptHWDP(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.nominal_size || ""} · ${it.weight_lbft} ppf HWDP · ${it.grade}`,
      od_in: it.od_in,
      id_in: it.id_in,
      weight_lbft: it.weight_lbft,
      grade: it.grade,
      connection_type: it.connection_type,
      notes: `HWDP body OD ${it.od_in} in · ID ${it.id_in} in. Tool-joint OD reserved for surge/swab.`,
    },
    "hwdp"
  );
}
function _adaptDrillCollar(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.nominal_size || ""} DC · ${it.weight_lbft} ppf`,
      od_in: it.od_in,
      id_in: it.id_in,
      weight_lbft: it.weight_lbft,
      grade: it.grade,
      connection_type: it.connection_type,
      notes: `Drill collar — OD ${it.od_in} in · ID ${it.id_in} in.`,
    },
    "drill_collar"
  );
}
function _adaptCasing(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.nominal_size || ""} · ${it.weight_lbft} ppf · ${it.grade}`,
      od_in: it.od_in,
      id_in: it.id_in,
      drift_diameter_in: it.drift_diameter_in,
      weight_lbft: it.weight_lbft,
      grade: it.grade,
      connection_type: it.connection_type,
      notes: `Casing OD ${it.od_in} in · ID ${it.id_in} in · drift ${it.drift_diameter_in} in.`,
    },
    "casing"
  );
}
function _adaptTubing(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.nominal_size || ""} tubing · ${it.weight_lbft} ppf · ${it.grade}`,
      od_in: it.od_in,
      id_in: it.id_in,
      weight_lbft: it.weight_lbft,
      grade: it.grade,
      connection_type: it.connection_type,
      notes: `Tubing OD ${it.od_in} in · ID ${it.id_in} in.`,
    },
    "tubing"
  );
}
function _adaptBit(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.diameter_in} in bit · TFA ${it.tfa_sqin} sq in · ${it.num_nozzles}n`,
      diameter_in: it.diameter_in,
      tfa_sqin: it.tfa_sqin,
      num_nozzles: it.num_nozzles,
      iadc_code: it.iadc_code || it.iadd_code,
      notes: it.notes || `Bit ${it.diameter_in} in.`,
    },
    "bit"
  );
}
function _adaptPump(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.model} · ${it.liner_size_in} in liner · ${it.pump_factor_bbl_stroke} bbl/stk`,
      model: it.model,
      liner_size_in: it.liner_size_in,
      pump_factor_bbl_stroke: it.pump_factor_bbl_stroke,
      volumetric_efficiency: it.volumetric_efficiency,
      max_pressure_psi: it.max_pressure_psi,
      max_flow_gpm: it.max_flow_gpm,
      notes: `${it.model} · ${it.liner_size_in} in liner — verify against rig pump card.`,
    },
    "pump"
  );
}
function _adaptMudMotor(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.od_in} in motor · ${it.bend_degrees}° bend`,
      od_in: it.od_in,
      bend_degrees: it.bend_degrees,
      max_flow_gpm: it.max_flow_gpm,
      max_torque_ftlb: it.max_torque_ftlb,
      max_rpm: it.max_rpm,
      notes: it.notes || `Mud motor — verify against tool ticket.`,
    },
    "mud_motor"
  );
}
function _adaptRSS(it) {
  return _decorateBase(
    {
      id: it.id,
      label: `${it.od_in} in RSS · DLS ≤ ${it.max_dogleg_deg100ft}°/100ft`,
      od_in: it.od_in,
      max_dogleg_deg100ft: it.max_dogleg_deg100ft,
      max_flow_gpm: it.max_flow_gpm,
      max_rpm: it.max_rpm,
      notes: it.notes || `RSS — verify against tool ticket.`,
    },
    "rotary_steerable"
  );
}
function _adaptBOPStack(it) {
  return _decorateBase(
    {
      id: it.id,
      label: it.name,
      name: it.name,
      type: it.type,
      pressure_rating_psi: it.pressure_rating_psi,
      bop_size_in: it.bop_size_in,
      rams: it.rams,
      annular: it.annular,
      choke_line_id_in: it.choke_line_id_in,
      kill_line_id_in: it.kill_line_id_in,
      accumulator_volume_gal: it.accumulator_volume_gal,
      accumulator_precharge_psi: it.accumulator_precharge_psi,
      notes: `${it.type === "subsea" ? "Subsea" : "Surface"} BOP stack — verify against rig BOP certification sheet.`,
    },
    "bop_stack"
  );
}
// R2c: formation_template_section flattens template.sections[*] into individual
// picker items. Each item id is a compound `${template.id}__${section.name_slug}`.
function _flattenFormationSections(template) {
  const slug = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, "_");
  return (template.sections || []).map((sec) => {
    const compoundId = `${template.id}__${slug(sec.name)}`;
    return _decorateBase(
      {
        id: compoundId,
        templateId: template.id,
        templateName: template.name,
        sectionName: sec.name,
        depth_ft: sec.depth_ft,
        hole_size_in: sec.hole_size_in,
        casing_size_in: sec.casing_size_in,
        mud_weight_ppg: sec.mud_weight_ppg,
        fracture_gradient_psi_ft: sec.fracture_gradient_psi_ft,
        label: `${template.name} · ${sec.name} · ${sec.depth_ft.toLocaleString()} ft TVD · ${sec.casing_size_in} in casing`,
        notes: `Planning section: MW ${sec.mud_weight_ppg} ppg · FG ${sec.fracture_gradient_psi_ft} psi/ft. Verify against the approved drilling program.`,
      },
      "formation_template_section"
    );
  });
}

export function getReferenceCategories() {
  const lib = _loadMergedLib();
  return [
    {
      id: "drill_pipe", label: "Drill Pipe",
      tagline: "Drill-pipe reference entries — verify against rig tally and manufacturer specification before use.",
      items: lib.equipment.drill_pipe.map(_adaptDrillPipe),
      libraryKinds: ["drill_pipe"],
      mode: "active",
    },
    {
      id: "hwdp", label: "Heavy Weight Drill Pipe",
      tagline: "HWDP reference entries — body OD populated; tool-joint OD is reserved for surge/swab calculations.",
      items: lib.equipment.heavy_weight_drill_pipe.map(_adaptHWDP),
      libraryKinds: ["hwdp"],
      mode: "active",
    },
    {
      id: "drill_collar", label: "Drill Collars",
      tagline: "Drill collar reference entries — verify against BHA tally.",
      items: lib.equipment.drill_collar.map(_adaptDrillCollar),
      libraryKinds: ["drill_collar"],
      mode: "active",
    },
    {
      id: "casing", label: "Casing",
      tagline: "Casing reference entries — verify against casing tally and manufacturer specification.",
      items: lib.equipment.casing.map(_adaptCasing),
      libraryKinds: ["casing"],
      mode: "active",
    },
    {
      id: "tubing", label: "Tubing",
      tagline: "Tubing reference entries — verify against tubing tally.",
      items: lib.equipment.tubing.map(_adaptTubing),
      libraryKinds: ["tubing"],
      mode: "active",
    },
    {
      id: "bit", label: "Bits",
      tagline: "Bit reference entries — diameters double as hole sizes for capacity calcs.",
      items: lib.equipment.bit.map(_adaptBit),
      libraryKinds: ["bit"],
      mode: "active",
    },
    {
      id: "pump", label: "Mud Pumps",
      tagline: "Pump factor (bbl/stk) per manufacturer card — verify against rig pump tally.",
      items: lib.pumps.map(_adaptPump),
      libraryKinds: ["pump"],
      mode: "active",
    },
    {
      id: "mud_motor", label: "Mud Motors",
      tagline: "BHA reference data — selecting a motor records provenance only; no numeric input is changed.",
      items: lib.equipment.mud_motor.map(_adaptMudMotor),
      libraryKinds: ["mud_motor"],
      mode: "active",
    },
    {
      id: "rotary_steerable", label: "Rotary Steerable",
      tagline: "BHA reference data — selecting an RSS records provenance only; no numeric input is changed.",
      items: lib.equipment.rotary_steerable.map(_adaptRSS),
      libraryKinds: ["rotary_steerable"],
      mode: "active",
    },
    {
      id: "bop_stack", label: "BOP Stacks",
      tagline: "Reference BOP stack data — requires mandatory confirmation against the rig's BOP certification sheet before any field is populated.",
      items: lib.bop_stacks.map(_adaptBOPStack),
      libraryKinds: ["bop_stack"],
      mode: "active",
      requiresSafetyConfirmation: true,
    },
    {
      id: "formation_template_section", label: "Formation Template Sections",
      tagline: "Reference planning sections — requires mandatory confirmation against the approved drilling program before any field is populated.",
      items: lib.formation_templates.flatMap(_flattenFormationSections),
      libraryKinds: ["formation_template_section"],
      mode: "active",
      requiresSafetyConfirmation: true,
    },
  ];
}

// Look up a library item by `kind` + `id` (used by LibraryPicker selection).
export function findLibraryItem(kind, id) {
  if (!kind || !id) return null;
  const cats = getReferenceCategories();
  const cat = cats.find((c) => c.id === kind);
  if (!cat) return null;
  return cat.items.find((it) => it.id === id) || null;
}

// Map a library item's geometry to the calculator field keys it can pre-fill.
// Returns an object whose keys are calculator-field keys (matching catalog.js
// field `key` strings) and whose values are the value to set + a libraryRef
// stub for provenance tagging. The CalculatorRunner consumes this.
//
// Returning `null` means "no input mutation" — used for BHA-tool kinds (motors,
// RSS) which only stamp provenance into a text/optional field.
export function fieldValuesFromLibraryItem(libraryKind, item, calcFieldKey) {
  if (!item) return null;
  const refStub = {
    id: item.id,
    label: item.label,
    source: item.source,
    version: item.version,
    libraryKind,
  };
  const _val = (v) => ({ value: String(v), libraryRef: refStub });

  if (libraryKind === "drill_pipe" || libraryKind === "hwdp") {
    // Body OD only — tool-joint OD is for future surge/swab.
    if (calcFieldKey === "dp" || calcFieldKey === "workstringOd" || calcFieldKey === "pipeOd") return _val(item.od_in);
    if (calcFieldKey === "workstringId" || calcFieldKey === "pipeId") return _val(item.id_in);
    // T0 — projection bug fix. The seed already stores weight/grade/connection
    // on every drill pipe and HWDP row; only OD/ID were being exposed. Expose
    // the rest so every calculator field that asks for them gets pre-filled.
    // No new data is invented — only existing properties are surfaced.
    if (calcFieldKey === "weight" || calcFieldKey === "weightLbFt" ||
        calcFieldKey === "pipeWeightLbFt" || calcFieldKey === "pipeWeight" ||
        calcFieldKey === "drillPipeWeight" || calcFieldKey === "hwdpWeight") {
      if (item.weight_lbft != null) return _val(item.weight_lbft);
    }
    if (calcFieldKey === "grade" || calcFieldKey === "pipeGrade") {
      if (item.grade) return _val(item.grade);
    }
    if (calcFieldKey === "connection" || calcFieldKey === "connectionType") {
      if (item.connection_type) return _val(item.connection_type);
    }
    return null;
  }
  if (libraryKind === "drill_collar") {
    if (calcFieldKey === "dp" || calcFieldKey === "pipeOd") return _val(item.od_in);
    if (calcFieldKey === "pipeId") return _val(item.id_in);
    // T0 — projection bug fix (same rationale as drill_pipe).
    if (calcFieldKey === "weight" || calcFieldKey === "weightLbFt" ||
        calcFieldKey === "pipeWeightLbFt" || calcFieldKey === "drillCollarWeight") {
      if (item.weight_lbft != null) return _val(item.weight_lbft);
    }
    if (calcFieldKey === "grade" || calcFieldKey === "pipeGrade") {
      if (item.grade) return _val(item.grade);
    }
    if (calcFieldKey === "connection" || calcFieldKey === "connectionType") {
      if (item.connection_type) return _val(item.connection_type);
    }
    return null;
  }
  if (libraryKind === "casing") {
    if (calcFieldKey === "dh" || calcFieldKey === "casingId" || calcFieldKey === "holeId") return _val(item.id_in);
    if (calcFieldKey === "casingOd") return _val(item.od_in);
    if (calcFieldKey === "drift") return _val(item.drift_diameter_in);
    if (calcFieldKey === "weight" || calcFieldKey === "casingWeight" ||
        calcFieldKey === "weightLbFt" || calcFieldKey === "pipeWeightLbFt") return _val(item.weight_lbft);
    // T0 — projection bug fix. Casing seed already stores grade + connection;
    // they were never exposed even though the data was sitting in the row.
    if (calcFieldKey === "grade" || calcFieldKey === "casingGrade") {
      if (item.grade) return _val(item.grade);
    }
    if (calcFieldKey === "connection" || calcFieldKey === "connectionType") {
      if (item.connection_type) return _val(item.connection_type);
    }
    return null;
  }
  if (libraryKind === "tubing") {
    if (calcFieldKey === "dp" || calcFieldKey === "pipeOd" || calcFieldKey === "tubingOd") return _val(item.od_in);
    if (calcFieldKey === "pipeId" || calcFieldKey === "tubingId") return _val(item.id_in);
    // T0 — projection bug fix (same rationale).
    if (calcFieldKey === "weight" || calcFieldKey === "tubingWeight" ||
        calcFieldKey === "weightLbFt" || calcFieldKey === "pipeWeightLbFt") {
      if (item.weight_lbft != null) return _val(item.weight_lbft);
    }
    if (calcFieldKey === "grade" || calcFieldKey === "tubingGrade" || calcFieldKey === "pipeGrade") {
      if (item.grade) return _val(item.grade);
    }
    if (calcFieldKey === "connection" || calcFieldKey === "connectionType") {
      if (item.connection_type) return _val(item.connection_type);
    }
    return null;
  }
  if (libraryKind === "bit") {
    if (calcFieldKey === "dh" || calcFieldKey === "holeId" || calcFieldKey === "bitDiameter") return _val(item.diameter_in);
    if (calcFieldKey === "tfa") return _val(item.tfa_sqin);
    if (calcFieldKey === "numNozzles") return _val(item.num_nozzles);
    return null;
  }
  if (libraryKind === "pump") {
    if (calcFieldKey === "pumpBblStk" || calcFieldKey === "pumpFactor" || calcFieldKey === "pumpOutputBblPerStk") {
      return { value: String(Number(item.pump_factor_bbl_stroke).toFixed(5)), libraryRef: refStub };
    }
    return null;
  }
  if (libraryKind === "mud_motor" || libraryKind === "rotary_steerable") {
    // BHA tool selection — populate the text field with the item ID itself so
    // the user sees their selection echoed back; no math input changes.
    if (calcFieldKey === "bhaTool") return _val(item.id);
    return null;
  }
  if (libraryKind === "formation_template_section") {
    // Safety-gated formation template section — populates the shoe TVD only.
    // No other math field is touched in R2c.
    if (calcFieldKey === "shoeTVD") return _val(item.depth_ft);
    return null;
  }
  if (libraryKind === "bop_stack") {
    // BOP stacks do NOT integrate into the CalculatorRunner field flow. They
    // are surfaced via BOPStackPicker on the BOP Test Sheet report. Return
    // null here so any accidental wiring is a no-op.
    return null;
  }
  return null;
}

// Short inline provenance pill — rendered next to a library-filled input.
// Spec'd in R2b plan, K.1.a: two lines on mobile, one line on desktop.
export function provenanceLineFromRef(ref) {
  if (!ref) return null;
  return `Loaded from ${ref.source} #${ref.id} · Verify against your tally`;
}

// Long-form provenance line — appended to result.steps for PDF/CSV export.
export function provenanceLineLongFromRef(ref) {
  if (!ref) return null;
  return `Reference value loaded from library item ${ref.id} (source: ${ref.source}, version ${ref.version}). Verify against rig/company/manufacturer data before use.`;
}

// =========================================================================
// Phase R2a — Merged Library typed selectors.
//
// All selectors below source from `merged_library_v1.json` via the lazy
// normalizer in `./normalize.js`. They run alongside (NOT replace) the pilot
// pickers that source from `tubulars.json` / `pumps.json`. The R2b workstream
// will migrate `LibraryPicker.jsx` to consume these selectors. R2a is data
// ingest only — no UI, no catalog changes.
// =========================================================================
export {
  REFERENCE_DATASET,
  REFERENCE_DATASET_VERSION,
  loadMergedLibrary,
  getMergedLibraryStats,
} from "./normalize";

import { loadMergedLibrary as _load } from "./normalize";

// --- Equipment selectors (partitioned by canonical type) ----------------
export function getDrillPipe()              { return _load().equipment.drill_pipe.slice(); }
export function getDrillCollars()           { return _load().equipment.drill_collar.slice(); }
export function getCasing()                 { return _load().equipment.casing.slice(); }
export function getTubing()                 { return _load().equipment.tubing.slice(); }
export function getBits()                   { return _load().equipment.bit.slice(); }
export function getHWDP()                   { return _load().equipment.heavy_weight_drill_pipe.slice(); }
export function getMudMotors()              { return _load().equipment.mud_motor.slice(); }
export function getRSS()                    { return _load().equipment.rotary_steerable.slice(); }
export function getMWD()                    { return _load().equipment.mwd_tool.slice(); }
export function getLWD()                    { return _load().equipment.lwd_tool.slice(); }
export function getStabilizers()            { return _load().equipment.stabilizer.slice(); }
export function getCrossovers()             { return _load().equipment.crossover.slice(); }

// --- Pumps / fluids / cement / BOP / formations / constants / thresholds -
export function getPumpsMerged()            { return _load().pumps.slice(); }
export function getBaseFluids()             { return _load().drilling_fluids.base_fluids.slice(); }
export function getMudAdditives()           { return _load().drilling_fluids.additives.slice(); }
export function getMudTemplates()           { return _load().drilling_fluids.mud_properties_templates.slice(); }
export function getCementSlurries()         { return _load().cement_slurries.slice(); }
export function getBOPStacks()              { return _load().bop_stacks.slice(); }
export function getFormationTemplates()     { return _load().formation_templates.slice(); }
export function getConstants()              { return _load().constants.slice(); }
export function getSafetyThresholds()       { return { ..._load().safety_thresholds.default }; }

// --- Universal lookup by ID across every collection ---------------------
export function byIdMerged(id) {
  if (!id) return null;
  const lib = _load();
  // Equipment buckets
  for (const bucket of Object.values(lib.equipment)) {
    if (!Array.isArray(bucket)) continue;
    const hit = bucket.find((r) => r.id === id);
    if (hit) return hit;
  }
  const lists = [
    lib.pumps,
    lib.drilling_fluids.base_fluids,
    lib.drilling_fluids.additives,
    lib.drilling_fluids.mud_properties_templates,
    lib.cement_slurries,
    lib.bop_stacks,
    lib.formation_templates,
    lib.constants,
  ];
  for (const list of lists) {
    const hit = list.find((r) => r.id === id);
    if (hit) return hit;
  }
  return null;
}

